import { useState, useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import axios from "axios";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  LinkIcon,
  ImageIcon,
  Undo,
  Redo,
  Upload,
  X,
  Trash2,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MenuButton = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-2 rounded hover:bg-its-teal-light/20 transition-colors ${
      isActive ? "bg-its-gold/20 text-its-gold" : "text-its-offwhite"
    }`}
  >
    {children}
  </button>
);

// ========================
// Media Library Modal
// ========================
export const MediaLibrary = ({ isOpen, onClose, onSelect, token }) => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [insertPosition, setInsertPosition] = useState("bottom");

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMedia(res.data);
    } catch {
      toast.error("Failed to load media library");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (isOpen) fetchMedia();
  }, [isOpen, fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      fetchMedia();
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMedia((prev) => prev.filter((m) => m.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleSelect = (item) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${item.storage_path}`;
    onClose();
    // Delay select to let modal unmount and editor regain focus
    setTimeout(() => onSelect(url, insertPosition), 100);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, pointerEvents: "auto", position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
      data-testid="media-library-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" style={{ position: "fixed", inset: 0 }} />
      <div
        className="relative bg-its-teal-dark border border-its-teal-light/20 rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl mx-4"
        style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "auto" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-its-teal-light/20">
          <h2 className="text-lg font-semibold text-white">Media Library</h2>
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-its-gold text-its-teal text-sm font-medium cursor-pointer hover:bg-its-gold-soft transition-colors"
              data-testid="media-upload-button"
            >
              <Upload size={16} />
              {uploading ? "Uploading..." : "Upload"}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <button onClick={onClose} className="p-2 text-its-offwhite/60 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Insert Position Selector */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-its-teal-light/10 bg-its-teal/30 flex-wrap" data-testid="insert-position-toggle">
          <span className="text-sm text-its-offwhite/70">Layout:</span>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: "top", label: "Full Width — Top" },
              { value: "bottom", label: "Full Width — Bottom" },
              { value: "left", label: "Left 33% — Text Wraps" },
              { value: "right", label: "Right 33% — Text Wraps" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setInsertPosition(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  insertPosition === opt.value
                    ? "bg-its-gold text-its-teal"
                    : "border border-its-teal-light/30 text-its-offwhite/60 hover:text-white hover:border-its-teal-light/50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-its-gold" />
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-12 text-its-offwhite/50">
              <ImageIcon size={48} className="mx-auto mb-3 opacity-30" />
              <p>No images uploaded yet.</p>
              <p className="text-sm mt-1">Click "Upload" to add images.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {media.map((item) => (
                <div
                  key={item.id}
                  className="group relative aspect-square rounded-lg overflow-hidden border-2 border-its-teal-light/10 hover:border-its-gold/50 transition-colors cursor-pointer"
                  data-testid={`media-item-${item.id}`}
                  onClick={() => handleSelect(item)}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${item.thumbnail_path}`}
                    alt={item.original_filename}
                    className="w-full h-full object-cover"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors pointer-events-none" />
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      className="p-1.5 bg-black/60 rounded text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pointer-events-none">
                    <p className="text-xs text-white truncate">{item.original_filename}</p>
                  </div>
                  {/* Selection indicator on hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="px-3 py-1.5 bg-its-gold text-its-teal text-sm font-medium rounded-lg shadow-lg">
                      Insert
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========================
// TipTap Editor
// ========================
export const TipTapEditor = ({ content, onChange, placeholder = "Start writing...", token: propToken, onOpenMediaLibrary }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const editorRef = useRef(null);

  // Get token from props or localStorage
  const token = propToken || localStorage.getItem("admin_token");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-its-teal-light underline hover:text-its-gold",
        },
        autolink: true,
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            target: {
              default: null,
              renderHTML(attributes) {
                const href = attributes.href || "";
                if (href.startsWith("/") || href.startsWith(window.location.origin)) {
                  return { target: "_self" };
                }
                return { target: "_blank", rel: "noopener noreferrer" };
              },
            },
          };
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-md my-4",
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (element) => element.getAttribute("style"),
              renderHTML: (attributes) => {
                if (!attributes.style) return {};
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor focus:outline-none",
      },
    },
  });

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleInsertImage = (url, position = "bottom") => {
    const ed = editorRef.current || editor;
    if (!ed) return;
    setTimeout(() => {
      if (position === "left" || position === "right") {
        const float = position === "left" ? "left" : "right";
        const margin = position === "left" ? "0 1rem 1rem 0" : "0 0 1rem 1rem";
        const style = `float:${float};width:33%;margin:${margin};border-radius:0.5rem;`;
        ed.chain().focus("start").setImage({ src: url, style }).run();
      } else if (position === "top") {
        ed.chain().focus("start").setImage({ src: url }).run();
      } else {
        ed.chain().focus("end").setImage({ src: url }).run();
      }
    }, 50);
  };

  const openMediaLibrary = () => {
    editorRef.current = editor;
    if (onOpenMediaLibrary) {
      // External mode: parent renders MediaLibrary outside Dialog
      onOpenMediaLibrary((url, position) => handleInsertImage(url, position));
    } else {
      // Self-contained mode
      setShowMediaLibrary(true);
    }
  };

  return (
    <>
      <div className="border border-its-teal-light/30 rounded-lg overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 bg-its-teal-dark border-b border-its-teal-light/20">
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title="Bold"
          >
            <Bold size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title="Italic"
          >
            <Italic size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-its-teal-light/20 mx-1" />

          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
          >
            <Heading1 size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
          >
            <Heading2 size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive("heading", { level: 3 })}
            title="Heading 3"
          >
            <Heading3 size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-its-teal-light/20 mx-1" />

          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title="Bullet List"
          >
            <List size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title="Numbered List"
          >
            <ListOrdered size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive("blockquote")}
            title="Quote"
          >
            <Quote size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-its-teal-light/20 mx-1" />

          <MenuButton onClick={addLink} isActive={editor.isActive("link")} title="Add Link">
            <LinkIcon size={18} />
          </MenuButton>
          <MenuButton onClick={openMediaLibrary} title="Insert Image from Media Library" data-testid="open-media-library-btn">
            <ImageIcon size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-its-teal-light/20 mx-1" />

          <MenuButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo size={18} />
          </MenuButton>
        </div>

        {/* Editor Content */}
        <EditorContent editor={editor} />
      </div>

      {/* Media Library Modal — only when self-contained (no parent handler) */}
      {!onOpenMediaLibrary && (
        <MediaLibrary
          isOpen={showMediaLibrary}
          onClose={() => setShowMediaLibrary(false)}
          onSelect={handleInsertImage}
          token={token}
        />
      )}
    </>
  );
};
